import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { homedir } from 'node:os';

export const MAX_AGE_24H_MS = 24 * 60 * 60 * 1000;

export const CLI_CACHE_FILE = 'update-check.json';
export const SKILL_CACHE_FILE = 'skill-update-check.json';
// Latest version of the PRIVATE wayai-admin skill. Filled by the background refresher via the
// platform-admin-gated endpoint (403s for non-admins → never written → no nudge), so it self-scopes.
export const ADMIN_SKILL_CACHE_FILE = 'admin-skill-update-check.json';

export interface VersionCache {
  lastCheck: number;
  latest: string | null;
}

export function getVersionCachePath(filename: string = CLI_CACHE_FILE): string {
  return join(homedir(), '.wayai', filename);
}

export function readVersionCache(filename: string = CLI_CACHE_FILE): VersionCache | null {
  try {
    const path = getVersionCachePath(filename);
    if (!existsSync(path)) return null;
    const parsed = JSON.parse(readFileSync(path, 'utf-8')) as VersionCache;
    if (typeof parsed.lastCheck !== 'number') return null;
    if (parsed.latest !== null && typeof parsed.latest !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readCachedLatest(
  filename: string = CLI_CACHE_FILE,
  maxAgeMs: number = MAX_AGE_24H_MS,
): string | null {
  const cache = readVersionCache(filename);
  if (!cache?.latest) return null;
  if (Date.now() - cache.lastCheck > maxAgeMs) return null;
  return cache.latest;
}

/** True when the cache is missing or older than `maxAgeMs` — i.e. due for a background refresh. */
export function isVersionCacheStale(
  filename: string = CLI_CACHE_FILE,
  maxAgeMs: number = MAX_AGE_24H_MS,
): boolean {
  const cache = readVersionCache(filename);
  if (!cache) return true;
  return Date.now() - cache.lastCheck > maxAgeMs;
}

export function writeVersionCache(filename: string, cache: VersionCache): void {
  const path = getVersionCachePath(filename);
  const dir = dirname(path);
  if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
  writeFileSync(path, JSON.stringify(cache));
}

/**
 * Bump `lastCheck` to now while preserving the last-known `latest`. Used as a sentinel so that
 * rapid concurrent foreground runs don't each spawn a background refresher within the TTL window.
 */
export function touchVersionCache(filename: string): void {
  writeVersionCache(filename, { lastCheck: Date.now(), latest: readVersionCache(filename)?.latest ?? null });
}
